import React from "react";
import cn from "classnames";
import Spinner from "@src/components/atoms/spinner/spinner";
import { TimezoneDate } from "@src/components/atoms/timezone-date/timezone-date";
import { useI18N, useLanguage } from "@src/language/language-context";
import { useMachineOverviewDrBoy } from "./use-machine-overview-dr-boy";
import type { DrBoyMachineOverviewDefinition } from "@src/types/factory-dashboard-dr-boy";
import type { ModeColor } from "@src/types/factory-dashboard";
import { formatDuration } from "@src/cloud-connect/vpn-manager/utils/format-duration-string";
import styles from "./machine-overview-container-dr-boy.module.scss";

type GridArea = "quarter" | "half";

interface MachineOverviewContainerDrBoyProps {
  machine: DrBoyMachineOverviewDefinition;
  gridArea: GridArea;
}

const cssClassName = "machine-overview-container";

const getModeBadgeStyle = (modeColor: ModeColor) => ({
  backgroundColor: modeColor.background,
  borderColor: modeColor.outline,
  color: modeColor.outline,
  outline: `1px solid ${modeColor.outline}`,
});

const DASH = "—";

const formatInteger = (value: number, locale: string) =>
  value.toLocaleString(locale);

const formatTimeRemaining = (hours: number): string => {
  const seconds = Math.max(0, Math.round(hours * 3600));
  return formatDuration(seconds);
};

export const MachineOverviewContainerDrBoy = ({
  machine,
  gridArea,
}: MachineOverviewContainerDrBoyProps) => {
  const i18n = useI18N();
  const language = useLanguage();
  const locale = language ?? "en";

  const {
    machineTitle,
    isLoading,
    hasData,
    modeLabel,
    modeColor,
    job,
    jobGoodPartsCounter,
    nominalParts,
    cycleTime,
    progress,
    animatedProgressPercentage,
    timeRemainingHours,
    effectivity,
    lastUpdatedTime,
  } = useMachineOverviewDrBoy(machine);

  const texts = {
    job: i18n("modules.machineOverviewContainerDrBoy.job"),
    targetPartQuantityGoodTotal: i18n(
      "modules.machineOverviewContainerDrBoy.targetPartQuantityGoodTotal",
    ),
    progress: i18n("modules.machineOverviewContainerDrBoy.progress"),
    cycleTime: i18n("modules.machineOverviewContainerDrBoy.cycleTime"),
    timeRemaining: i18n("modules.machineOverviewContainerDrBoy.timeRemaining"),
    effectivity: i18n("modules.machineOverviewContainerDrBoy.effectivity"),
    noDataAvailable: i18n(
      "modules.machineOverviewContainerDrBoy.noDataAvailable",
    ),
    lastUpdated: i18n("modules.machineOverviewContainerDrBoy.lastUpdated"),
    secondsShort: i18n("modules.machineOverviewContainerDrBoy.secondsShort"),
  };

  const targetPartQuantity =
    jobGoodPartsCounter !== null && nominalParts !== null
      ? `${formatInteger(jobGoodPartsCounter, locale)} / ${formatInteger(
          nominalParts,
          locale,
        )}`
      : DASH;

  const progressText = progress !== null ? `${progress.toFixed(1)}%` : DASH;
  const cycleTimeText =
    cycleTime !== null
      ? `${formatInteger(cycleTime, locale)} ${texts.secondsShort}`
      : DASH;
  const timeRemainingText =
    timeRemainingHours !== null
      ? formatTimeRemaining(timeRemainingHours)
      : DASH;
  const effectivityText =
    effectivity !== null ? `${effectivity.toFixed(1)}%` : DASH;

  return (
    <section
      className={cn(styles[cssClassName], {
        [styles[`${cssClassName}--grid-quarter`]]: gridArea === "quarter",
        [styles[`${cssClassName}--grid-half`]]: gridArea === "half",
        [styles[`${cssClassName}--loading`]]: isLoading,
      })}
    >
      {isLoading ? (
        <div className={styles[`${cssClassName}__spinner-wrapper`]}>
          <Spinner />
        </div>
      ) : !hasData ? (
        <>
          <h3 className={styles[`${cssClassName}__title`]}>{machineTitle}</h3>
          <div className={styles[`${cssClassName}__no-data`]}>
            {texts.noDataAvailable}
          </div>
        </>
      ) : (
        <>
          <h3 className={styles[`${cssClassName}__title`]}>{machineTitle}</h3>

          <button
            className={styles[`${cssClassName}__badge`]}
            style={getModeBadgeStyle(modeColor)}
            type="button"
          >
            {modeLabel}
          </button>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>{texts.job}</span>
            <span className={styles[`${cssClassName}__value`]}>{job}</span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.targetPartQuantityGoodTotal}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {targetPartQuantity}
            </span>
          </div>

          <div className={styles[`${cssClassName}__progress-track`]}>
            <div
              className={styles[`${cssClassName}__progress-fill`]}
              style={{ width: `${animatedProgressPercentage}%` }}
            />
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.progress}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {progressText}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.cycleTime}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {cycleTimeText}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.timeRemaining}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {timeRemainingText}
            </span>
          </div>

          <div className={styles[`${cssClassName}__row`]}>
            <span className={styles[`${cssClassName}__key`]}>
              {texts.effectivity}
            </span>
            <span className={styles[`${cssClassName}__value`]}>
              {effectivityText}
            </span>
          </div>
        </>
      )}

      {lastUpdatedTime && (
        <p className={styles[`${cssClassName}__updated-at`]}>
          {texts.lastUpdated}: <TimezoneDate date={lastUpdatedTime} />
        </p>
      )}
    </section>
  );
};
